/* =====================================================================
 * WorkspaceAppRouter — shared top-level route tree.
 *
 * Phase 11-3c hoist: the `<Suspense>` + `<Routes>` skeleton that used
 * to live inline in `askyourai/src/routes/AppRouter.tsx` is now a
 * reusable component. Host apps (askyourai, apps/enterprise) just
 * supply:
 *
 *   - `publicRoutes`: array of `{ path, element }` for unauthenticated
 *     surfaces (auth-callback, share, terms, privacy, pricing, ...).
 *   - `authGuardElement`: `<AuthGuard />` — still owned by the host so
 *     it can read the host's auth store directly.
 *   - `authenticatedLayoutElement`: `<SEOHead /> + <WorkspaceLayout />`
 *     combo, rendered under `authGuardElement`.
 *   - `authRoutes`: tenant-resolved workspace route list (B2C:
 *     `ROUTE_ENTRIES` from `routeConfig.ts`; each entry becomes both a
 *     `path` route and an optional `path/:id` route; hosts may attach
 *     route-first layout elements to either shape).
 *   - `authExtraRoutes`: auxiliary authenticated routes that do not
 *     fit the `authRoutes` shape (e.g. `/settings` redirect).
 *   - `defaultPath`: `resolveTenantDefaultPath(tenant)` — used for both
 *     the `/` redirect and the `*` 404 fallback.
 *   - `invitePath` / `inviteElement`: optional team-invite leaf that
 *     still runs through `authGuardElement`.
 *
 * **No tenant or store imports here** — every tenant decision is made
 * in the host via the standard `@auw/tenant` helpers and handed in as
 * already-resolved strings / elements. That keeps `@auw/workspace-app`
 * free of the B2C-specific auth vault / billing / plugin bridges that
 * still live inside `askyourai`.
 *
 * The component deliberately mirrors the existing B2C tree *exactly*
 * so the pre-hoist snapshot tests and SEO crawl paths keep matching.
 * ===================================================================== */

import React, { Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

/**
 * Authenticated workspace route entry. Mirrors `RouteEntry` in
 * `askyourai/src/routes/routeConfig.ts` without dragging in the B2C
 * SEO typing — the router does not need to know the i18n key, only
 * the URL shape and optional route element.
 */
export interface WorkspaceRouteEntry {
  /** URL path prefix (e.g. `/agents`). */
  readonly path: string;
  /** Whether this route supports a trailing `/:id` parameter. */
  readonly hasIdParam: boolean;
  /** Optional route-first element for the base path. Defaults to `null`. */
  readonly element?: ReactNode;
  /** Optional route-first element for `path/:id`. Defaults to `element`. */
  readonly idElement?: ReactNode;
}

export interface WorkspacePublicRouteEntry {
  readonly path: string;
  readonly element: ReactNode;
}

export interface WorkspaceAuthExtraRouteEntry {
  readonly path: string;
  readonly element: ReactNode;
}

export interface WorkspaceAppRouterProps {
  /** Routes served without the auth guard (callback, share, legal, ...). */
  readonly publicRoutes: readonly WorkspacePublicRouteEntry[];
  /**
   * Element rendered as the parent `<Route element={...}>` for every
   * authenticated subtree. Expected to be the host's `<AuthGuard />`
   * (it handles the `isAuthed / authChecking / inviteToken` branches).
   */
  readonly authGuardElement: ReactNode;
  /**
   * Element rendered as the parent `<Route element={...}>` under the
   * auth guard. Expected to be the host's `<AuthenticatedLayout />`
   * (SEOHead + WorkspaceLayout).
   */
  readonly authenticatedLayoutElement: ReactNode;
  /** Workspace mode routes (`/agents`, `/docs`, …). */
  readonly authRoutes: readonly WorkspaceRouteEntry[];
  /**
   * Authenticated routes that do not follow the `{ path, hasIdParam }`
   * template. Used for one-offs like `/settings` → `/settings/team`
   * redirects.
   */
  readonly authExtraRoutes?: readonly WorkspaceAuthExtraRouteEntry[];
  /** Tenant-resolved landing path used by both `/` and `*` fallbacks. */
  readonly defaultPath: string;
  /** Team invite path template (`/invite/team/:token`). Optional — a
   *  tenant that disables invites entirely can omit this pair. */
  readonly invitePath?: string;
  /** Element rendered at `invitePath` after the auth guard. */
  readonly inviteElement?: ReactNode;
  /** Fallback UI shown while lazy-loaded public / auth pages resolve. */
  readonly suspenseFallback?: ReactNode;
}

/**
 * Renders the full top-level route tree. See file header for the
 * adapter shape. The tree layout is identical to the pre-hoist B2C
 * version so SEO crawlers (and any route-count-asserting tests)
 * observe the same paths post-hoist.
 */
export function WorkspaceAppRouter({
  publicRoutes,
  authGuardElement,
  authenticatedLayoutElement,
  authRoutes,
  authExtraRoutes,
  defaultPath,
  invitePath,
  inviteElement,
  suspenseFallback,
}: WorkspaceAppRouterProps): JSX.Element {
  return (
    <Suspense fallback={suspenseFallback ?? null}>
      <Routes>
        {/* Public routes — no auth guard, lazy-loaded by the host. */}
        {publicRoutes.map((entry) => (
          <Route key={entry.path} path={entry.path} element={entry.element} />
        ))}

        {/* Team invite leaf. The guard still runs so logged-out users
         *  see the host's InviteHomePage, and logged-in users hit the
         *  tenant-configured redirect element. */}
        {invitePath && inviteElement ? (
          <Route path={invitePath} element={authGuardElement}>
            <Route index element={inviteElement} />
          </Route>
        ) : null}

        {/* Authenticated routes. */}
        <Route element={authGuardElement}>
          <Route element={authenticatedLayoutElement}>
            {authRoutes.map((entry) => (
              <React.Fragment key={entry.path}>
                <Route path={entry.path} element={entry.element ?? null} />
                {entry.hasIdParam ? (
                  <Route
                    path={`${entry.path}/:id`}
                    element={entry.idElement ?? entry.element ?? null}
                  />
                ) : null}
              </React.Fragment>
            ))}
            {authExtraRoutes?.map((entry) => (
              <Route
                key={entry.path}
                path={entry.path}
                element={entry.element}
              />
            ))}
          </Route>

          {/* Root `/` → tenant landing. Kept under the guard so
           *  unauthenticated hits still flow through AuthGuard. */}
          <Route path="/" element={<Navigate to={defaultPath} replace />} />
        </Route>

        {/* 404 fallback → tenant landing. Matches pre-hoist behaviour. */}
        <Route path="*" element={<Navigate to={defaultPath} replace />} />
      </Routes>
    </Suspense>
  );
}
